import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Archive, ArchiveRestore, CheckCircle2, ChevronDown, ChevronUp, Download, ExternalLink, FileText, Lock, MapPin, Plus, Send, Unlock, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { fmtDT, gstatus } from "@/components/graphisme/gUtils";

export const CREATION_STATUS = {
  a_faire: { label: "À faire", cls: "bg-neutral-100 text-neutral-600 border-neutral-200" },
  en_cours: { label: "En cours", cls: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  prete: { label: "Prête", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  envoyee_validation: { label: "Envoyée pour validation", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  modifications_demandees: { label: "Modifications demandées", cls: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  validee: { label: "Validée", cls: "bg-green-50 text-green-700 border-green-200" },
  finalisee: { label: "Finalisée", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
};

export const KIND_META = {
  travail: { label: "Travail (interne)", cls: "text-neutral-400" },
  apercu: { label: "Aperçu client", cls: "text-blue-600" },
  livraison: { label: "Livraison HD", cls: "text-green-700" },
};

const ANNOTATION_LABEL = { ouverte: "Ouverte", traitee: "Traitée", resolue: "Résolue" };
const sel = "border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white";

export default function GraphismeCreation() {
  const { pid, cid } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [vOpen, setVOpen] = useState(false);
  const [vf, setVf] = useState({ kind: "apercu", label: "", comment: "", external_url: "", file: null });
  const [addFileVid, setAddFileVid] = useState(null);
  const [af, setAf] = useState({ kind: "livraison", external_url: "", file: null });
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [dFiles, setDFiles] = useState([]);
  const [showArchived, setShowArchived] = useState(false);

  const authorize = async (mode) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("mode", mode);
      if (mode === "upload") {
        if (dFiles.length === 0) { toast.error("Choisissez au moins un fichier HD"); setBusy(false); return; }
        dFiles.forEach((f) => fd.append("files", f));
      }
      await api.post(`/graphisme/projects/${pid}/creations/${cid}/delivery/authorize`, fd);
      toast.success("Livraison autorisée — le client peut télécharger le(s) fichier(s) final(aux) HD");
      setDeliveryOpen(false); setDFiles([]); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const fileRef2 = useRef(null);

  const load = () => api.get(`/graphisme/projects/${pid}`).then((r) => setP(r.data)).catch(() => setP(false));
  useEffect(() => { load(); }, [pid]);

  if (p === null) return <div className="p-10 text-center"><div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin mx-auto" /></div>;
  if (p === false) return <div className="p-10 text-center text-neutral-500">Projet introuvable.</div>;
  const c = (p.creations || []).find((x) => x.id === cid);
  if (!c) return <div className="p-10 text-center text-neutral-500">Création introuvable.</div>;
  const st = CREATION_STATUS[c.status] || CREATION_STATUS.a_faire;
  const latest = (c.versions || []).slice(-1)[0];
  const preview = latest?.files?.find((f) => f.kind === "apercu" && !f.external_url &&
    (/^image\/(png|jpe?g|gif|webp)$/.test(f.content_type || "") || f.content_type === "application/pdf"));

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const uploadVersion = () => act(async () => {
    const fd = new FormData();
    if (vf.file) fd.append("file", vf.file);
    fd.append("kind", vf.kind);
    fd.append("label", vf.label);
    fd.append("comment", vf.comment);
    fd.append("external_url", vf.external_url);
    await api.post(`/graphisme/projects/${pid}/creations/${cid}/versions`, fd);
    setVOpen(false);
    setVf({ kind: "apercu", label: "", comment: "", external_url: "", file: null });
  }, "Version ajoutée");

  const uploadFile = () => act(async () => {
    const fd = new FormData();
    if (af.file) fd.append("file", af.file);
    fd.append("kind", af.kind);
    fd.append("external_url", af.external_url);
    await api.post(`/graphisme/projects/${pid}/creations/${cid}/versions/${addFileVid}/files`, fd);
    setAddFileVid(null);
    setAf({ kind: "livraison", external_url: "", file: null });
  }, "Fichier ajouté");

  const cmUrl = (fid) => `${API}/graphisme/files/${fid}`;

  const moveVersion = (vid, dir) => {
    const ids = (c.versions || []).map((v) => v.id);
    const i = ids.indexOf(vid);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    act(() => api.post(`/graphisme/projects/${pid}/creations/${cid}/versions/reorder`, { order: ids }), null);
  };

  return (
    <div className="space-y-5" data-testid="gcreation-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <button onClick={() => navigate(`/graphisme/projet/${pid}`)} className="text-xs text-neutral-400 hover:text-neutral-900 transition-colors">← {p.name}</button>
          <h1 className="font-heading text-2xl font-bold mt-1 flex items-center gap-3 flex-wrap">
            {c.name}
            <span className={`text-[11px] rounded-full px-2.5 py-1 border font-normal ${st.cls}`} data-testid="gcreation-status">{st.label}</span>
          </h1>
          <p className="text-sm text-neutral-500 mt-0.5">{[c.format, c.dimensions].filter(Boolean).join(" · ")} · {latest ? `Version ${latest.label}` : "Aucune version"}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" className="bg-neutral-900 hover:bg-neutral-800 text-white gap-1.5" onClick={() => setVOpen(true)} data-testid="gcreation-add-version">
            <Plus className="w-3.5 h-3.5" /> Ajouter une version
          </Button>
          {c.status !== "envoyee_validation" && c.status !== "validee" && c.status !== "finalisee" && (
            <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5" disabled={busy} data-testid="gcreation-send-validation"
              onClick={() => act(() => api.post(`/graphisme/projects/${pid}/creations/${cid}/send-validation`), "Création envoyée pour validation — client notifié")}>
              <Send className="w-3.5 h-3.5" /> Envoyer pour validation
            </Button>
          )}
          {c.status !== "archivee" && (
            <Button size="sm" className="bg-green-700 hover:bg-green-800 text-white gap-1.5" disabled={busy} data-testid="gcreation-authorize-delivery"
              onClick={() => setDeliveryOpen(true)}>
              <Upload className="w-3.5 h-3.5" /> {c.status === "finalisee" ? "Ajouter des fichiers finaux" : "Finaliser et livrer"}
            </Button>
          )}
          {!["validee", "finalisee", "archivee"].includes(c.status) && (
            <Button size="sm" variant="outline" className="gap-1.5 border-green-300 text-green-700 hover:bg-green-50" disabled={busy} data-testid="gcreation-validate-agency"
              onClick={() => {
                if (!window.confirm("Le client a validé cette création hors plateforme (WhatsApp, téléphone…) ? Cela équivaut à une validation client et débloque la livraison.")) return;
                act(() => api.post(`/graphisme/projects/${pid}/creations/${cid}/validate-agency`), "Création marquée comme validée par le client");
              }}>
              <CheckCircle2 className="w-3.5 h-3.5" /> Validation reçue (hors plateforme)
            </Button>
          )}
          {c.status === "finalisee" && (
            <span className="text-xs bg-green-50 text-green-700 border border-green-200 rounded-full px-3 py-1.5" data-testid="gcreation-finalized">
              ✓ Version finale disponible côté client{c.delivery?.final_files?.length > 1 ? ` — ${c.delivery.final_files.length} fichiers` : c.delivery?.final_name ? ` — ${c.delivery.final_name}` : ""}
            </span>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white border border-neutral-200 rounded-xl p-5" data-testid="gcreation-preview">
            <h3 className="font-heading font-bold mb-3">Aperçu {latest ? `— ${latest.label}` : ""}</h3>
            {preview ? (
              preview.content_type === "application/pdf" ? (
                <iframe src={cmUrl(preview.id)} title="Aperçu PDF" className="w-full h-[480px] border border-neutral-200 rounded-lg" data-testid="gcreation-preview-pdf" />
              ) : (
                <img src={cmUrl(preview.id)} alt="Aperçu" className="max-h-[480px] w-auto mx-auto rounded-lg border border-neutral-100" data-testid="gcreation-preview-img" />
              )
            ) : (
              <p className="text-sm text-neutral-400 text-center py-10">Aucun aperçu — ajoutez un fichier « Aperçu client » (JPG, PNG ou PDF) à la dernière version.</p>
            )}
            {latest?.comment && <p className="text-xs text-neutral-500 mt-2 whitespace-pre-line">{latest.comment}</p>}
          </div>

          <div className="bg-white border border-neutral-200 rounded-xl p-5" data-testid="gcreation-annotations">
            <h3 className="font-heading font-bold mb-3 flex items-center gap-2"><MapPin className="w-4 h-4" /> Annotations client ({(c.annotations || []).length})</h3>
            {(c.annotations || []).length === 0 && <p className="text-sm text-neutral-400">Aucune annotation — le client peut commenter directement sur l'aperçu.</p>}
            <div className="space-y-2">
              {(c.annotations || []).map((a) => (
                <div key={a.id} className="flex items-start gap-3 border border-neutral-100 rounded-lg p-3 text-sm" data-testid={`gannotation-${a.id}`}>
                  <MapPin className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p>{a.text}</p>
                    <p className="text-[11px] text-neutral-400">{a.by} · {fmtDT(a.at)}</p>
                  </div>
                  <select value={a.status} disabled={busy} data-testid={`gannotation-status-${a.id}`}
                    onChange={(e) => act(() => api.patch(`/graphisme/projects/${pid}/creations/${cid}/annotations/${a.id}`, { status: e.target.value }), null)}
                    className={`${sel} !py-1 !px-2 text-xs`}>
                    {Object.entries(ANNOTATION_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>

          {(c.mod_requests || []).length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4" data-testid="gcreation-modrequests">
              <h3 className="font-heading font-bold text-yellow-800 mb-2">Demandes de modification</h3>
              {c.mod_requests.map((m) => (
                <div key={m.id} className="text-sm border-l-2 border-yellow-400 pl-3 mb-2">
                  <p className="whitespace-pre-line">{m.message || "(fichier joint)"}</p>
                  <p className="text-[11px] text-yellow-600">{fmtDT(m.at)}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-5">
          {(c.delivery?.authorized || (c.delivery?.files || []).length > 0) && (
            <div className="bg-green-50/50 border border-green-200 rounded-xl p-5" data-testid="gcreation-delivered-files">
              <h3 className="font-heading font-bold mb-1 flex items-center gap-2 text-green-800"><CheckCircle2 className="w-4 h-4" /> Fichiers livrés au client</h3>
              <p className="text-[11px] text-green-700/70 mb-2.5">
                Livrés le {fmtDT(c.delivery?.finalized_at)} — l'agence y a accès à tout moment, même après livraison.
              </p>
              <div className="space-y-1">
                {(c.delivery?.files || []).map((f) => (
                  <div key={f.id} className="flex items-center gap-2 text-xs bg-white border border-green-100 rounded-lg px-3 py-1.5" data-testid={`gdelivered-${f.id}`}>
                    <FileText className="w-3 h-3 text-green-600 shrink-0" />
                    {f.external_url ? (
                      <a href={f.external_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline truncate flex-1 flex items-center gap-1">
                        {f.name} <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : (
                      <a href={cmUrl(f.id)} target="_blank" rel="noreferrer" className="hover:underline truncate flex-1 font-medium" data-testid={`gdelivered-dl-${f.id}`}>{f.name}</a>
                    )}
                    <span className="text-[10px] text-neutral-400 shrink-0">{fmtDT(f.at)}</span>
                  </div>
                ))}
                {(c.delivery?.final_files || []).filter((x) => !(c.delivery?.files || []).some((f) => f.id === x.id)).map((x) => (
                  <div key={x.id} className="flex items-center gap-2 text-xs bg-white border border-green-100 rounded-lg px-3 py-1.5" data-testid={`gdelivered-${x.id}`}>
                    <FileText className="w-3 h-3 text-green-600 shrink-0" />
                    <a href={cmUrl(x.id)} target="_blank" rel="noreferrer" className="hover:underline truncate flex-1 font-medium" data-testid={`gdelivered-dl-${x.id}`}>{x.name}</a>
                    <span className="text-[10px] text-neutral-400 shrink-0">depuis une version</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="bg-white border border-neutral-200 rounded-xl p-5" data-testid="gcreation-versions">
            <h3 className="font-heading font-bold mb-3">Versions ({(c.versions || []).filter((v) => !v.archived).length})</h3>
            {[...(c.versions || [])].filter((v) => !v.archived).reverse().map((v) => (
              <div key={v.id} className="border border-neutral-100 rounded-lg p-3 mb-2" data-testid={`gversion-${v.id}`}>
                <div className="flex justify-between items-center mb-1.5">
                  <p className="text-sm font-semibold">{v.label}</p>
                  <div className="flex items-center gap-0.5">
                    <span className="text-[11px] text-neutral-400 mr-1">{fmtDT(v.at)}</span>
                    <button title="Monter" data-testid={`gversion-up-${v.id}`} onClick={() => moveVersion(v.id, 1)}
                      className="text-neutral-300 hover:text-neutral-900 transition-colors"><ChevronUp className="w-3.5 h-3.5" /></button>
                    <button title="Descendre" data-testid={`gversion-down-${v.id}`} onClick={() => moveVersion(v.id, -1)}
                      className="text-neutral-300 hover:text-neutral-900 transition-colors"><ChevronDown className="w-3.5 h-3.5" /></button>
                    <button title="Archiver cette version" data-testid={`gversion-archive-${v.id}`} disabled={busy}
                      onClick={() => act(() => api.patch(`/graphisme/projects/${pid}/creations/${cid}/versions/${v.id}`, { archived: true }), "Version archivée")}
                      className="text-neutral-300 hover:text-orange-600 transition-colors"><Archive className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                <div className="space-y-1">
                  {v.files.map((f) => (
                    <div key={f.id} className="flex items-center gap-2 text-xs" data-testid={`gfile-${f.id}`}>
                      {KIND_META[f.kind]?.label === "Travail (interne)" ? <Lock className="w-3 h-3 text-neutral-300" /> : <FileText className="w-3 h-3 text-neutral-400" />}
                      {f.external_url ? (
                        <a href={f.external_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline truncate flex-1 flex items-center gap-1">
                          {f.name} <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <a href={cmUrl(f.id)} target="_blank" rel="noreferrer" className="hover:underline truncate flex-1">{f.name}</a>
                      )}
                      <span className={KIND_META[f.kind]?.cls}>{KIND_META[f.kind]?.label}</span>
                      {(c.delivery?.final_files || []).some((x) => x.id === f.id) ? (
                        <span className="text-[10px] text-green-700 font-semibold shrink-0" data-testid={`gfile-isfinal-${f.id}`}>✓ Final</span>
                      ) : ["validee", "finalisee"].includes(c.status) && !f.external_url && f.kind !== "travail" && (
                        <button title="Marquer comme fichier final (téléchargeable immédiatement par le client)" data-testid={`gfile-makefinal-${f.id}`} disabled={busy}
                          onClick={() => act(async () => {
                            const fd = new FormData();
                            fd.append("mode", "upload");
                            fd.append("file_ids", f.id);
                            await api.post(`/graphisme/projects/${pid}/creations/${cid}/delivery/authorize`, fd);
                          }, "Fichier marqué comme final — téléchargeable par le client")}
                          className="text-neutral-300 hover:text-green-600 transition-colors shrink-0">
                          <CheckCircle2 className="w-3 h-3" />
                        </button>
                      )}
                      {f.kind === "livraison" && !(p.unlock?.project) && !(p.unlock?.files || []).includes(f.id) && !(p.unlock?.creations || []).includes(cid) && (
                        <button title="Autoriser le téléchargement sans filigrane (tracé)" data-testid={`gunlock-file-${f.id}`}
                          onClick={() => act(() => api.post(`/graphisme/projects/${pid}/unlock`, { scope: "file", target_id: f.id }), "Téléchargement sans filigrane autorisé pour ce fichier")}
                          className="text-neutral-300 hover:text-green-600 transition-colors">
                          <Unlock className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button onClick={() => setAddFileVid(v.id)} className="text-[11px] text-neutral-400 hover:text-neutral-900 mt-1.5 transition-colors" data-testid={`gversion-addfile-${v.id}`}>
                  + Ajouter un fichier / lien
                </button>
                {addFileVid === v.id && (
                  <div className="mt-2 pt-2 border-t border-neutral-100 space-y-2">
                    <select className={`${sel} w-full !py-1.5 text-xs`} value={af.kind} onChange={(e) => setAf({ ...af, kind: e.target.value })} data-testid="gaddfile-kind">
                      <option value="livraison">Livraison HD (fichier final client)</option>
                      <option value="apercu">Aperçu client</option>
                      <option value="travail">Travail (interne uniquement)</option>
                    </select>
                    <input type="file" ref={fileRef2} className="text-xs w-full" onChange={(e) => setAf({ ...af, file: e.target.files[0] })} data-testid="gaddfile-file" />
                    <Input placeholder="ou lien externe https://…" value={af.external_url} onChange={(e) => setAf({ ...af, external_url: e.target.value })} className="!py-1.5 text-xs" data-testid="gaddfile-url" />
                    <div className="flex gap-1.5">
                      <Button size="sm" disabled={busy || (!af.file && !af.external_url)} onClick={uploadFile} className="text-xs bg-neutral-900 text-white" data-testid="gaddfile-save">Ajouter</Button>
                      <Button size="sm" variant="outline" className="text-xs" onClick={() => setAddFileVid(null)}>Annuler</Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {(c.versions || []).some((v) => v.archived) && (
              <div className="mt-3 pt-3 border-t border-neutral-100">
                <button onClick={() => setShowArchived((v) => !v)} data-testid="gversions-archived-toggle"
                  className="text-[11px] text-neutral-400 hover:text-neutral-900 transition-colors">
                  Versions archivées ({(c.versions || []).filter((v) => v.archived).length}) {showArchived ? "▾" : "▸"}
                </button>
                {showArchived && (c.versions || []).filter((v) => v.archived).map((v) => (
                  <div key={v.id} className="flex items-center justify-between border border-neutral-100 rounded-lg p-2.5 mt-1.5 opacity-60" data-testid={`gversion-archived-${v.id}`}>
                    <p className="text-xs text-neutral-500">{v.label} · {fmtDT(v.at)}</p>
                    <button title="Restaurer cette version" data-testid={`gversion-restore-${v.id}`} disabled={busy}
                      onClick={() => act(() => api.patch(`/graphisme/projects/${pid}/creations/${cid}/versions/${v.id}`, { archived: false }), "Version restaurée")}
                      className="text-neutral-400 hover:text-green-600 transition-colors"><ArchiveRestore className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
            {(c.versions || []).length === 0 && <p className="text-sm text-neutral-400">Aucune version pour l'instant.</p>}
          </div>

          <div className="bg-white border border-neutral-200 rounded-xl p-5 text-sm space-y-2" data-testid="gcreation-meta">
            <h3 className="font-heading font-bold">Suivi</h3>
            <p className="flex justify-between"><span className="text-neutral-400">Corrections</span><strong>{p.corrections_used} / {p.corrections_included} utilisées</strong></p>
            {p.corrections_used > p.corrections_included && (
              <p className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded-lg p-2" data-testid="gcreation-quota-exceeded">
                Quota d'allers-retours dépassé — un devis complémentaire peut être créé depuis la page projet.
              </p>
            )}
            <p className="flex justify-between"><span className="text-neutral-400">Envoyée le</span><span>{c.sent_validation_at ? fmtDT(c.sent_validation_at) : "—"}</span></p>
            <p className="flex justify-between"><span className="text-neutral-400">Validée le</span><span>{c.validated_at ? fmtDT(c.validated_at) : "—"}</span></p>
            <div className="pt-2 border-t border-neutral-100 flex items-center justify-between gap-2 flex-wrap">
              <span className="text-neutral-400 text-xs">Validation BAT (impression)</span>
              {c.bat ? (
                <span className="text-green-700 text-xs font-semibold" data-testid="gcreation-bat-ok">
                  BAT VALIDÉ le {fmtDT(c.bat.at)} ({c.bat.version_label}) par {c.bat.by}
                </span>
              ) : (
                <button disabled={busy} data-testid="gcreation-bat-toggle"
                  onClick={() => act(() => api.patch(`/graphisme/projects/${pid}/creations/${cid}`, { bat_mode: !c.bat_mode }), c.bat_mode ? "BAT désactivé" : "Validation BAT activée")}
                  className="text-xs text-neutral-500 hover:text-neutral-900 border border-neutral-200 rounded-full px-2.5 py-1 transition-colors">
                  {c.bat_mode ? "BAT activé — désactiver" : "Activer la validation BAT"}
                </button>
              )}
            </div>
          </div>

          <div className="bg-white border border-neutral-200 rounded-xl p-5" data-testid="gcreation-unlock">
            <h3 className="font-heading font-bold mb-1 flex items-center gap-2"><Unlock className="w-4 h-4" /> Exception de livraison</h3>
            <p className="text-xs text-neutral-400 mb-2">Autoriser le téléchargement sans filigrane avant règlement (tracé dans l'historique).</p>
            <Button variant="outline" size="sm" disabled={busy} data-testid="gunlock-creation"
              onClick={() => act(() => api.post(`/graphisme/projects/${pid}/unlock`, { scope: "creation", target_id: cid }), "Téléchargement sans filigrane autorisé pour cette création")}>
              Autoriser pour cette création
            </Button>
            {(p.unlock?.project || (p.unlock?.creations || []).includes(cid)) && (
              <p className="text-xs text-green-700 mt-2" data-testid="gunlock-state">✓ Téléchargement sans filigrane actif {(p.unlock || {}).project ? "(projet entier)" : "(cette création)"}</p>
            )}
          </div>
        </div>
      </div>

      {vOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setVOpen(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md space-y-3" onClick={(e) => e.stopPropagation()} data-testid="gversion-modal">
            <h3 className="font-heading font-bold">Nouvelle version — {(c.versions || []).length + 1}</h3>
            <div>
              <Label className="text-xs">Niveau du fichier</Label>
              <select className={`${sel} w-full mt-1`} value={vf.kind} onChange={(e) => setVf({ ...vf, kind: e.target.value })} data-testid="gversion-kind">
                <option value="apercu">Aperçu client</option>
                <option value="livraison">Livraison HD (fichier final client)</option>
                <option value="travail">Travail (interne uniquement)</option>
              </select>
            </div>
            <div><Label className="text-xs">Libellé (défaut : V{(c.versions || []).length + 1})</Label>
              <Input className="mt-1" value={vf.label} onChange={(e) => setVf({ ...vf, label: e.target.value })} placeholder="V1, V2, BAT final…" data-testid="gversion-label" /></div>
            <div><Label className="text-xs">Commentaire de version</Label>
              <Input className="mt-1" value={vf.comment} onChange={(e) => setVf({ ...vf, comment: e.target.value })} data-testid="gversion-comment" /></div>
            <input type="file" ref={fileRef} className="text-sm w-full" onChange={(e) => setVf({ ...vf, file: e.target.files[0] })} data-testid="gversion-file" />
            <Input placeholder="ou lien externe sécurisé https://…" value={vf.external_url} onChange={(e) => setVf({ ...vf, external_url: e.target.value })} data-testid="gversion-url" />
            <Button onClick={uploadVersion} disabled={busy || (!vf.file && !vf.external_url)} className="w-full bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="gversion-save">
              <Upload className="w-4 h-4 mr-1.5" /> {busy ? "Envoi…" : "Ajouter la version"}
            </Button>
          </div>
        </div>
      )}

      {deliveryOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setDeliveryOpen(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md space-y-4" onClick={(e) => e.stopPropagation()} data-testid="gdelivery-modal">
            <h3 className="font-heading font-bold">Finaliser et livrer — « {c.name} »</h3>
            {!["validee", "finalisee"].includes(c.status) && (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5" data-testid="gdelivery-force-warning">
                Cette création n'a pas été validée dans l'espace client. La finaliser revient à confirmer
                que le client a validé hors plateforme (WhatsApp, téléphone…) — le statut passera à
                « Validée » puis « Finalisée » et le client sera notifié.
              </p>
            )}
            <p className="text-sm text-neutral-500">Quel fichier souhaitez-vous livrer au client ?</p>
            <div className="border border-neutral-200 rounded-xl p-4 space-y-2">
              <p className="text-sm font-medium">Option 1 — Utiliser le fichier actuel comme version finale</p>
              <p className="text-xs text-neutral-400">Le fichier actuellement présenté ({latest?.label || "—"}) devient la version finale HD téléchargeable, sans filigrane.</p>
              <Button size="sm" variant="outline" className="w-full" disabled={busy || !latest} data-testid="gdelivery-use-current"
                onClick={() => authorize("current")}>
                Utiliser ce fichier comme version finale
              </Button>
            </div>
            <div className="border border-neutral-200 rounded-xl p-4 space-y-2">
              <p className="text-sm font-medium">Option 2 — Ajouter un ou plusieurs fichiers finaux HD</p>
              <p className="text-xs text-neutral-400">Importez des fichiers destinés uniquement à la livraison (PSD, PDF HD, PNG HD, AI, EPS, SVG…) — inutile de les rattacher à une version.</p>
              <input type="file" multiple className="text-xs w-full" onChange={(e) => setDFiles(Array.from(e.target.files || []))} data-testid="gdelivery-file" />
              {dFiles.length > 0 && <p className="text-[11px] text-neutral-500">{dFiles.length} fichier(s) : {dFiles.map((f) => f.name).join(", ")}</p>}
              <Button size="sm" variant="outline" className="w-full" disabled={busy || dFiles.length === 0} data-testid="gdelivery-upload"
                onClick={() => authorize("upload")}>
                Importer et livrer {dFiles.length > 1 ? "ces fichiers HD" : "ce fichier HD"}
              </Button>
            </div>
            <p className="text-[11px] text-neutral-400">Tant que cette étape n'est pas confirmée, aucun téléchargement HD n'est disponible côté client.</p>
          </div>
        </div>
      )}
    </div>
  );
}
